// flameco-auth React bindings (skill v3). Wrap the app in <FlamecoAuthProvider>,
// then use useFlamecoAuth(), <RequireAuth>, <RequireTag> and <Can>.
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import * as flameco from './flameco-auth';
import type { FlamecoAuthOptions, FlamecoUser } from './flameco-auth';

interface FlamecoAuthValue {
  /** False until the saved session has been restored in the browser (also during server rendering). */
  ready: boolean;
  user: FlamecoUser | null;
  /** Switch-over only: someone is signed in through the app's OLD login. */
  legacySignedIn: boolean;
  /**
   * Flameco users: true when their token carries `tag`.
   * Old-login users (switch-over): true, so the app's old role checks decide.
   */
  can: (tag: string) => boolean;
  signIn: typeof flameco.signIn;
  signOut: typeof flameco.signOut;
  authFetch: typeof flameco.authFetch;
}

const Ctx = createContext<FlamecoAuthValue | null>(null);

export function FlamecoAuthProvider({
  options,
  legacySignedIn = false,
  legacyReady = true,
  children,
}: {
  options: FlamecoAuthOptions;
  /** Switch-over only: pass true while someone is signed in with the old login. Remove in the clean-up run. */
  legacySignedIn?: boolean;
  /** Switch-over only: false while the old login is still restoring its session (async), so guards wait instead of flashing the login screen. */
  legacyReady?: boolean;
  children: React.ReactNode;
}) {
  // Restore the session in an effect, not during render: the server (Next.js)
  // has no localStorage, and the first browser render must match its HTML.
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<FlamecoUser | null>(null);
  const optionsRef = useRef(options);
  useEffect(() => {
    flameco.configure(optionsRef.current);
    setUser(flameco.getUser());
    setReady(true);
    return flameco.onChange(setUser);
  }, []);
  const value: FlamecoAuthValue = {
    ready: ready && legacyReady,
    user,
    legacySignedIn: !user && legacySignedIn,
    can: (tag) => (user ? user.tags.includes(tag) : legacySignedIn),
    signIn: flameco.signIn,
    signOut: flameco.signOut,
    authFetch: flameco.authFetch,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFlamecoAuth(): FlamecoAuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useFlamecoAuth must be used inside <FlamecoAuthProvider>');
  return v;
}

/**
 * Signed-in only. Without `loginScreen`, signed-out people go straight to the
 * Flameco login page. Switch-over: pass the app's login screen ("Sign in with
 * Flameco" + "Use old sign-in"); old-login users count as signed in.
 */
export function RequireAuth({ children, loginScreen }: { children: React.ReactNode; loginScreen?: React.ReactNode }) {
  const { ready, user, legacySignedIn, signIn } = useFlamecoAuth();
  const signedIn = !!user || legacySignedIn;
  useEffect(() => {
    if (ready && !signedIn && !loginScreen) signIn().catch(() => {});
  }, [ready, signedIn, loginScreen, signIn]);
  if (!ready) return null;
  if (signedIn) return <>{children}</>;
  return <>{loginScreen ?? <p style={{ padding: 24 }}>Signing in…</p>}</>;
}

/** Renders children only for people with `tag`; otherwise `fallback` (default: a short notice). */
export function RequireTag({ tag, children, fallback }: { tag: string; children: React.ReactNode; fallback?: React.ReactNode }) {
  const { ready, can } = useFlamecoAuth();
  if (!ready) return null;
  if (can(tag)) return <>{children}</>;
  return <>{fallback ?? <p style={{ padding: 24 }}>You don't have access to this. Ask an administrator.</p>}</>;
}

/** Inline show/hide for buttons, menu items, columns: <Can tag="work-orders.complete">…</Can> */
export function Can({ tag, children }: { tag: string; children: React.ReactNode }) {
  const { can } = useFlamecoAuth();
  return can(tag) ? <>{children}</> : null;
}

/** Mount at the callback route (e.g. /auth/callback), outside every guard. */
export function FlamecoCallback({ onDone }: { onDone: (returnTo: string) => void }) {
  const [error, setError] = useState('');
  const ran = useRef(false); // React StrictMode runs effects twice; a code works once
  const { ready } = useFlamecoAuth();
  useEffect(() => {
    if (!ready || ran.current) return;
    ran.current = true;
    flameco
      .handleCallback()
      .then(({ returnTo }) => onDone(returnTo))
      // A silent check found nobody: on to the login page, landing where the
      // person was going (never back on this callback, which would loop).
      .catch((e) => (e instanceof flameco.LoginRequiredError ? flameco.signIn({ returnTo: e.returnTo }) : setError(e.message)))
      .catch((e) => setError(e?.message || 'Sign-in could not start.'));
  }, [ready, onDone]);
  return (
    <p style={{ padding: 24 }}>
      {error ? `${error} ` : 'Finishing sign-in…'}
      {error && <button onClick={() => flameco.signIn({ returnTo: '/' })}>Try again</button>}
    </p>
  );
}
